import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "../../hooks/use-toast";
import { fmtINR } from "../../lib/format";
import { usePortfolio } from "../../context/PortfolioContext";
import {
  Save,
  Trash2,
  Plus,
  X,
  Calculator,
  Users,
  Download,
} from "lucide-react";

const newId = () => `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

function currentFY() {
  const now = new Date();
  const y = now.getFullYear();
  const startYear = now.getMonth() >= 3 ? y : y - 1;
  return `FY ${startYear}-${startYear + 1}`;
}

// Parse "FY 2025-2026" (or "2025-2026") → 2025. Returns null if unparseable.
function fyStartYear(fy) {
  if (!fy) return null;
  const m = String(fy).match(/(\d{4})\s*-\s*(\d{2,4})/);
  return m ? parseInt(m[1], 10) : null;
}

// For FY YYYY-(YYYY+1), snapshot = 31 Mar (YYYY+1) (last day of the FY)
function fySnapshotDate(fy) {
  const sy = fyStartYear(fy);
  return sy ? `${sy + 1}-03-31` : null;
}

export default function BonusForm() {
  const { toast } = useToast();
  const { reload: reloadPortfolio } = usePortfolio();
  const [statements, setStatements] = useState([]);
  const [fy, setFy] = useState(currentFY());
  const [equityPAT, setEquityPAT] = useState(0);
  const [derivativePAT, setDerivativePAT] = useState(0);
  const [closingCapital, setClosingCapital] = useState(0);
  const [closingUnits, setClosingUnits] = useState(0);
  const [unitRateOnApril, setUnitRateOnApril] = useState(100);
  const [bonusPoolOverride, setBonusPoolOverride] = useState(""); // string — empty = auto
  const [members, setMembers] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pulling, setPulling] = useState(false);

  const loadList = async () => {
    try {
      const rows = await api.listBonus();
      setStatements(rows);
    } catch (e) {}
  };
  useEffect(() => { loadList(); }, []);

  useEffect(() => {
    const existing = statements.find((s) => s.fiscalYear === fy);
    if (existing) {
      setEquityPAT(existing.equityPAT ?? 0);
      setDerivativePAT(existing.derivativePAT ?? 0);
      setClosingCapital(existing.closingCapital ?? 0);
      setClosingUnits(existing.closingUnits ?? 0);
      setUnitRateOnApril(existing.unitRateOnApril ?? 100);
      setBonusPoolOverride(
        existing.bonusPoolUnitsInput == null ? "" : String(existing.bonusPoolUnitsInput)
      );
      setMembers(
        (existing.members || []).map((m) => ({
          id: m.id || newId(),
          memberId: m.memberId || null,
          name: m.name || "",
          units: m.units ?? 0,
        }))
      );
      setNotes(existing.notes || "");
    } else {
      setEquityPAT(0);
      setDerivativePAT(0);
      setClosingCapital(0);
      setClosingUnits(0);
      setUnitRateOnApril(100);
      setBonusPoolOverride("");
      setMembers([]);
      setNotes("");
    }
  }, [fy, statements]);

  const computed = useMemo(() => {
    const totalPAT = (parseFloat(equityPAT) || 0) + (parseFloat(derivativePAT) || 0);
    const ur = parseFloat(unitRateOnApril) || 0;
    const cap = parseFloat(closingCapital) || 0;
    const totalBonusUnitsGenerated = ur > 0 ? totalPAT / ur : 0;
    const bonusPctFY = cap > 0 ? (totalPAT / cap) * 100 : 0;
    const poolRaw = bonusPoolOverride === "" ? null : parseFloat(bonusPoolOverride);
    const poolUnits = poolRaw == null || isNaN(poolRaw) ? totalBonusUnitsGenerated : poolRaw;
    const totalMembersUnits = members.reduce((a, m) => a + (parseFloat(m.units) || 0), 0);

    const enriched = members.map((m) => {
      const u = parseFloat(m.units) || 0;
      const pct = totalMembersUnits > 0 ? (u / totalMembersUnits) * 100 : 0;
      const bonus = totalMembersUnits > 0 ? (u / totalMembersUnits) * poolUnits : 0;
      return { ...m, holdingPct: pct, bonusIssued: bonus };
    });
    const totalBonusIssued = enriched.reduce((a, m) => a + m.bonusIssued, 0);

    return { totalPAT, totalBonusUnitsGenerated, bonusPctFY, poolUnits, totalMembersUnits, enriched, totalBonusIssued };
  }, [equityPAT, derivativePAT, closingCapital, unitRateOnApril, bonusPoolOverride, members]);

  const pullFromPnl = async () => {
    setPulling(true);
    try {
      const [eq, der] = await Promise.all([
        api.getPnl(fy).catch(() => null),
        api.getDerivativePnl(fy).catch(() => null),
      ]);
      if (eq) setEquityPAT(eq.pat ?? 0);
      if (der) setDerivativePAT(der.pat ?? 0);
      if (!eq && !der) {
        toast({ title: "No P&L found", description: `Create Equity/Derivative P&L for ${fy} first` });
      } else {
        toast({
          title: "Pulled PAT",
          description: `Equity ${fmtINR(eq?.pat || 0, { decimals: 2 })} · Derivative ${fmtINR(der?.pat || 0, { decimals: 2 })}`,
        });
      }
    } catch (e) {
      toast({ title: "Pull failed", description: e.response?.data?.detail || "Try again" });
    }
    setPulling(false);
  };

  const pullMembers = async () => {
    const snapDate = fySnapshotDate(fy);
    try {
      if (snapDate) {
        // Historical snapshot: units & capital outstanding as of end of FY
        const snap = await api.unitsSnapshot(snapDate);
        const rows = (snap.members || []).filter((m) => (m.units || 0) !== 0);
        if (rows.length === 0) {
          // Fallback to current balances if no history yet for that date
          const current = await api.members();
          setMembers(
            current.map((m) => ({
              id: newId(),
              memberId: m.id,
              name: m.name,
              units: m.units ?? 0,
            }))
          );
          setClosingUnits(
            current.reduce((a, m) => a + (parseFloat(m.units) || 0), 0)
          );
          setClosingCapital(
            current.reduce((a, m) => a + (parseFloat(m.capitalInvested) || 0), 0)
          );
          toast({
            title: `No history on ${snapDate}`,
            description: `Loaded current balances instead (${current.length} members).`,
          });
          return;
        }
        setMembers(
          rows.map((m) => ({
            id: newId(),
            memberId: m.memberId,
            name: m.name,
            units: m.units ?? 0,
          }))
        );
        setClosingUnits(snap.totalUnits || 0);
        setClosingCapital(snap.totalCapital || 0);
        toast({
          title: `Loaded as on ${snapDate}`,
          description: `${rows.length} members · ${Number(
            snap.totalUnits || 0
          ).toFixed(4)} units · ${fmtINR(snap.totalCapital || 0)}`,
        });
      } else {
        const rows = await api.members();
        setMembers(
          rows.map((m) => ({
            id: newId(),
            memberId: m.id,
            name: m.name,
            units: m.units ?? 0,
          }))
        );
        toast({ title: "Loaded", description: `${rows.length} members imported` });
      }
    } catch (e) {
      toast({ title: "Load failed", description: e.response?.data?.detail || "Try again" });
    }
  };

  const updateMember = (id, field, v) =>
    setMembers((ms) =>
      ms.map((m) =>
        m.id === id
          ? { ...m, [field]: field === "units" ? (v === "" ? 0 : parseFloat(v) || 0) : v }
          : m
      )
    );
  const addMember = () =>
    setMembers((ms) => [...ms, { id: newId(), memberId: null, name: "", units: 0 }]);
  const removeMember = (id) => setMembers((ms) => ms.filter((m) => m.id !== id));

  const save = async () => {
    if (!fy.trim()) {
      toast({ title: "Fiscal year required" });
      return;
    }
    setBusy(true);
    try {
      await api.upsertBonus({
        fiscalYear: fy.trim(),
        equityPAT: parseFloat(equityPAT) || 0,
        derivativePAT: parseFloat(derivativePAT) || 0,
        closingCapital: parseFloat(closingCapital) || 0,
        closingUnits: parseFloat(closingUnits) || 0,
        unitRateOnApril: parseFloat(unitRateOnApril) || 0,
        bonusPoolUnits: bonusPoolOverride === "" ? null : parseFloat(bonusPoolOverride) || 0,
        members: members.map((m) => ({
          id: m.id,
          memberId: m.memberId || null,
          name: m.name,
          units: parseFloat(m.units) || 0,
        })),
        notes,
      });
      toast({
        title: "Saved",
        description: `${fy} — Total Bonus Issued ${computed.totalBonusIssued.toFixed(4)} units`,
      });
      await loadList();
      // Refresh global portfolio so Bonus page, Dashboard, Members reflect new bonus tx
      try { await reloadPortfolio(); } catch (e) {}
    } catch (e) {
      toast({ title: "Save failed", description: e.response?.data?.detail || "Try again" });
    }
    setBusy(false);
  };

  const remove = async () => {
    const existing = statements.find((s) => s.fiscalYear === fy);
    if (!existing) return;
    if (!window.confirm(`Delete Bonus for ${fy}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.deleteBonus(fy);
      toast({ title: "Deleted", description: `${fy} removed` });
      await loadList();
      try { await reloadPortfolio(); } catch (e) {}
      setFy(currentFY());
    } catch (e) {
      toast({ title: "Delete failed", description: e.response?.data?.detail || "Try again" });
    }
    setDeleting(false);
  };

  const existing = statements.find((s) => s.fiscalYear === fy);

  return (
    <div className="grid lg:grid-cols-5 gap-5" data-testid="bonus-admin-form">
      <div className="lg:col-span-3 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6">
        <div className="text-sm font-semibold text-white">Bonus Statement</div>
        <div className="text-xs text-slate-500">
          All fields below are editable. Totals auto-compute per the bonus formula.
        </div>

        {/* FY selector + pull PAT */}
        <div className="mt-5">
          <Label className="text-slate-300 text-xs uppercase tracking-wider">Fiscal Year</Label>
          <div className="mt-1.5 flex gap-2 flex-wrap items-center">
            <Input
              value={fy}
              onChange={(e) => setFy(e.target.value)}
              placeholder="FY 2025-2026"
              data-testid="bonus-fy-input"
              className="bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500 max-w-xs"
            />
            {statements.map((s) => (
              <button
                key={s.fiscalYear}
                onClick={() => setFy(s.fiscalYear)}
                className={`px-3 h-9 rounded-md text-xs font-medium border whitespace-nowrap ${
                  s.fiscalYear === fy
                    ? "bg-emerald-500 text-slate-950 border-emerald-500"
                    : "bg-slate-950 text-slate-400 border-slate-800 hover:text-white"
                }`}
              >
                {s.fiscalYear}
              </button>
            ))}
          </div>
        </div>

        {/* Inputs */}
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          <NumField label="Equity PAT" value={equityPAT} onChange={setEquityPAT} testid="bonus-equity-pat" />
          <NumField label="Derivative PAT" value={derivativePAT} onChange={setDerivativePAT} testid="bonus-der-pat" />
          <div className="sm:col-span-2">
            <Button
              onClick={pullFromPnl}
              disabled={pulling}
              variant="ghost"
              data-testid="bonus-pull-pat"
              className="h-9 px-3 text-xs border border-slate-800 bg-slate-950 hover:bg-slate-900 text-slate-300"
            >
              <Download size={12} className="mr-1.5" />
              {pulling ? "Pulling…" : "Pull PAT from Equity & Derivative P&L"}
            </Button>
          </div>

          <NumField
            label={`Closing Capital (as on ${fySnapshotDate(fy) || "31 Mar"})`}
            value={closingCapital}
            onChange={setClosingCapital}
            testid="bonus-closing-capital"
          />
          <NumField
            label={`Closing Units (as on ${fySnapshotDate(fy) || "31 Mar"})`}
            value={closingUnits}
            onChange={setClosingUnits}
            testid="bonus-closing-units"
          />
          <NumField
            label="Unit rate on 1st April"
            value={unitRateOnApril}
            onChange={setUnitRateOnApril}
            testid="bonus-unit-rate"
          />
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-wider">
              Bonus Pool Units (override)
            </Label>
            <Input
              type="number"
              step="0.01"
              value={bonusPoolOverride}
              onChange={(e) => setBonusPoolOverride(e.target.value)}
              placeholder={`Auto: ${computed.totalBonusUnitsGenerated.toFixed(4)}`}
              data-testid="bonus-pool-override"
              className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500 font-mono"
            />
            <div className="mt-1 text-[11px] text-slate-500">
              Leave empty to auto-use Total Bonus Units Generated.
            </div>
          </div>
        </div>

        {/* Members */}
        <div className="mt-8" data-testid="bonus-members-section">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-emerald-400">
              Members &amp; Unit Balances
            </div>
            <Button
              onClick={pullMembers}
              variant="ghost"
              data-testid="bonus-pull-members"
              className="h-8 px-3 text-xs border border-slate-800 bg-slate-950 hover:bg-slate-900 text-slate-300"
            >
              <Users size={12} className="mr-1.5" />
              {fySnapshotDate(fy)
                ? `Load units as on ${fySnapshotDate(fy)}`
                : "Load from members"}
            </Button>
          </div>

          <div className="rounded-lg border border-slate-800 overflow-hidden">
            <div className="grid grid-cols-12 gap-2 bg-slate-950/40 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
              <div className="col-span-5">Member Name</div>
              <div className="col-span-3 text-right">Unit Balance</div>
              <div className="col-span-2 text-right">Holding %</div>
              <div className="col-span-1 text-right">Bonus</div>
              <div className="col-span-1" />
            </div>
            {computed.enriched.length === 0 && (
              <div className="px-3 py-4 text-xs text-slate-500 italic text-center">
                No members. Click "Load from members" or "Add entry" below.
              </div>
            )}
            {computed.enriched.map((r) => (
              <div
                key={r.id}
                className="grid grid-cols-12 gap-2 items-center px-3 py-1.5 border-b border-slate-800/70 last:border-0"
                data-testid={`bonus-member-row-${r.id}`}
              >
                <Input
                  value={r.name}
                  onChange={(e) => updateMember(r.id, "name", e.target.value)}
                  data-testid={`bonus-member-name-${r.id}`}
                  className="col-span-5 h-9 bg-slate-950 border-slate-800 text-white text-xs focus-visible:ring-emerald-500"
                />
                <Input
                  type="number"
                  step="0.0001"
                  value={r.units}
                  onChange={(e) => updateMember(r.id, "units", e.target.value)}
                  data-testid={`bonus-member-units-${r.id}`}
                  className="col-span-3 h-9 bg-slate-950 border-slate-800 text-white text-right font-mono text-xs focus-visible:ring-emerald-500"
                />
                <div className="col-span-2 text-right font-mono text-xs text-slate-400">
                  {r.holdingPct.toFixed(2)}%
                </div>
                <div className="col-span-1 text-right font-mono text-xs text-amber-300">
                  {r.bonusIssued.toFixed(4)}
                </div>
                <button
                  onClick={() => removeMember(r.id)}
                  data-testid={`bonus-member-remove-${r.id}`}
                  className="col-span-1 h-9 rounded-md hover:bg-rose-500/10 text-slate-500 hover:text-rose-400 flex items-center justify-center"
                  title="Remove"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <div className="grid grid-cols-12 gap-2 bg-slate-950/60 px-3 py-2 font-semibold text-sm">
              <div className="col-span-5 text-slate-100">Total</div>
              <div className="col-span-3 text-right font-mono text-emerald-400">
                {computed.totalMembersUnits.toFixed(4)}
              </div>
              <div className="col-span-2 text-right font-mono text-emerald-400">100.00%</div>
              <div className="col-span-1 text-right font-mono text-amber-300">
                {computed.totalBonusIssued.toFixed(4)}
              </div>
              <div className="col-span-1" />
            </div>
          </div>
          <button
            onClick={addMember}
            data-testid="bonus-member-add"
            className="mt-2 inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white border border-dashed border-slate-700 hover:border-slate-500 rounded-md px-3 py-1.5"
          >
            <Plus size={12} /> Add entry
          </button>
        </div>

        {/* Notes */}
        <div className="mt-6">
          <Label className="text-slate-300 text-xs uppercase tracking-wider">Notes (optional)</Label>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            data-testid="bonus-notes"
            className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500"
          />
        </div>

        <div className="mt-6 flex gap-3 flex-wrap">
          <Button
            onClick={save}
            disabled={busy}
            data-testid="bonus-save"
            className="h-11 px-6 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold disabled:opacity-60"
          >
            <Save className="mr-2" size={16} />
            {busy ? "Saving…" : existing ? "Update bonus" : "Save bonus"}
          </Button>
          {existing && (
            <Button
              onClick={remove}
              disabled={deleting}
              data-testid="bonus-delete"
              variant="ghost"
              className="h-11 px-4 border border-rose-500/40 bg-rose-500/5 text-rose-300 hover:bg-rose-500/10"
            >
              <Trash2 className="mr-2" size={14} />
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl p-5 md:p-6 h-fit sticky top-6">
        <div className="flex items-center gap-2 text-emerald-400">
          <Calculator size={16} />
          <div className="text-sm font-semibold">Bonus summary</div>
        </div>
        <dl className="mt-4 text-sm space-y-2">
          <KV k="Total PAT" v={fmtINR(computed.totalPAT)} positive bold />
          <KV
            k="Total Bonus Units Generated"
            v={computed.totalBonusUnitsGenerated.toFixed(4)}
          />
          <KV k={`Bonus for ${fy}`} v={`${computed.bonusPctFY.toFixed(2)}%`} />
          <div className="h-px bg-slate-800 my-2" />
          <KV k="Bonus Pool Units (distributing)" v={computed.poolUnits.toFixed(4)} bold />
          <KV k="Total Members Units" v={computed.totalMembersUnits.toFixed(4)} />
          <KV k="Total Bonus Issued" v={computed.totalBonusIssued.toFixed(4)} bold positive />
        </dl>
        <div className="mt-3 text-[11px] text-slate-500 space-y-1">
          <div>Total Bonus Units = Total PAT ÷ Unit rate</div>
          <div>Bonus % = Total PAT ÷ Closing Capital × 100</div>
          <div>Member Bonus = Units ÷ Total Units × Pool</div>
        </div>
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, testid, step = "0.01" }) {
  return (
    <div>
      <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
      <Input
        type="number"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 bg-slate-950 border-slate-800 text-white focus-visible:ring-emerald-500 font-mono"
      />
    </div>
  );
}

function KV({ k, v, positive, negative, bold }) {
  const color = positive ? "text-emerald-400" : negative ? "text-rose-400" : "text-slate-200";
  return (
    <div className="flex items-center justify-between">
      <dt className="text-slate-400 text-xs">{k}</dt>
      <dd className={`text-sm font-mono ${bold ? "font-semibold text-white" : color}`}>{v}</dd>
    </div>
  );
}
